/**
 * Additional Service Content - Incorporations, Tax & GST, Licenses
 */

// ==================== INCORPORATIONS ====================

export const llpRegistration = {
  id: "llp-registration",
  slug: "llp-registration",
  seoTitle: "LLP Registration Online | Limited Liability Partnership India",
  seoDescription: "Register your LLP with expert guidance. Combine partnership flexibility with limited liability. Professional LLP incorporation services at competitive rates.",
  heroTitle: "LLP Registration",
  heroSubtitle: "Business Incorporation",
  summary: "Limited Liability Partnership (LLP) combines the benefits of a partnership with limited liability protection for partners. It's ideal for professionals, service providers, and small businesses seeking flexibility with asset protection. LLP has lower compliance requirements than private limited companies while offering similar liability shield.",
  
  whoNeeds: {
    title: "Who Should Register an LLP?",
    description: "LLP is ideal for professionals and businesses seeking partnership flexibility with limited liability.",
    points: [
      "Professional service firms (CAs, lawyers, consultants)",
      "Small and medium businesses",
      "Startups not seeking equity funding",
      "Family businesses wanting limited liability",
      "Joint ventures between companies",
      "Businesses converting from partnership firms",
      "Professionals practicing in groups",
      "Service-based businesses with multiple owners"
    ]
  },
  
  whyRequired: {
    title: "Why Choose LLP Structure?",
    description: "LLP offers unique advantages combining partnership flexibility with corporate protection.",
    points: [
      "Limited Liability: Partners' personal assets protected from business debts.",
      "Lower Compliance: Fewer regulatory requirements compared to private limited companies.",
      "No Minimum Capital: Can be formed without any minimum capital requirement.",
      "Tax Efficiency: No dividend distribution tax; partners taxed individually.",
      "Flexibility: Partnership agreement can customize profit sharing and management.",
      "Professional Suitability: Ideal structure recognized for professional services."
    ]
  },
  
  benefits: [
    { title: "Limited Liability", description: "Partners protected from each other's misconduct and business debts." },
    { title: "No Minimum Capital", description: "Start with any capital amount; no minimum requirement." },
    { title: "Lower Compliance", description: "Only annual return and accounts filing required." },
    { title: "No DDT", description: "No dividend distribution tax on profit sharing among partners." },
    { title: "Separate Entity", description: "LLP is distinct from partners; can own property and sue." },
    { title: "Easy Transfer", description: "Partnership interests transferable without complex procedures." },
    { title: "Perpetual Succession", description: "LLP continues regardless of partner changes." },
    { title: "Flexible Agreement", description: "Partners can customize rights and obligations through agreement." }
  ],
  
  documentsRequired: {
    title: "Documents Required for LLP",
    description: "Documentation needed for LLP incorporation under LLP Act, 2008.",
    directors: [
      "PAN card of all partners (minimum 2)",
      "Aadhaar/Voter ID/Passport for identity",
      "Address proof (bank statement/utility bill)",
      "Passport-size photographs",
      "Proof of residence (not older than 2 months)"
    ],
    company: [
      "Proposed LLP names (2-3 unique options)",
      "Registered office address proof",
      "Rent agreement/ownership proof",
      "NOC from property owner",
      "Details of capital contribution",
      "Description of proposed business"
    ]
  },
  
  process: [
    { step: 1, title: "DSC Procurement", description: "Obtain Digital Signature Certificates for all designated partners.", duration: "1-2 days" },
    { step: 2, title: "DPIN Application", description: "Apply for Designated Partner Identification Number through DIR-3 KYC.", duration: "1-2 days" },
    { step: 3, title: "Name Reservation", description: "Reserve LLP name through RUN-LLP service on MCA portal.", duration: "2-3 days" },
    { step: 4, title: "FiLLiP Filing", description: "File Form for incorporation of LLP with partner details and address.", duration: "1 day" },
    { step: 5, title: "LLP Agreement", description: "Draft and file LLP Agreement within 30 days of incorporation.", duration: "3-5 days" },
    { step: 6, title: "Certificate & PAN", description: "Receive Certificate of Incorporation, PAN, and TAN.", duration: "3-5 days" }
  ],
  
  timeline: {
    total: "10-15 business days",
    breakdown: [
      { phase: "DSC & DPIN", duration: "2-3 days" },
      { phase: "Name approval", duration: "2-3 days" },
      { phase: "Incorporation filing", duration: "3-5 days" },
      { phase: "LLP agreement filing", duration: "3-5 days" }
    ],
    note: "LLP Agreement must be filed within 30 days of incorporation. Non-filing attracts penalties."
  },
  
  pricing: {
    title: "LLP Registration Pricing",
    description: "Transparent pricing for complete LLP incorporation.",
    packages: [
      {
        name: "Basic",
        price: "₹5,999",
        description: "For 2 partners LLP",
        features: [
          "2 DSC included",
          "DPIN for 2 partners",
          "Name reservation",
          "FiLLiP incorporation",
          "PAN & TAN"
        ]
      },
      {
        name: "Standard",
        price: "₹9,999",
        description: "With LLP Agreement",
        features: [
          "Everything in Basic",
          "LLP Agreement drafting",
          "Agreement filing",
          "Company stamp",
          "GST registration"
        ],
        popular: true
      },
      {
        name: "Premium",
        price: "₹17,999",
        description: "Complete business setup",
        features: [
          "Everything in Standard",
          "MSME registration",
          "Trademark filing",
          "First year compliance",
          "Dedicated support"
        ]
      }
    ],
    governmentFees: "Government fees approximately ₹1,500-3,000 based on capital contribution."
  },
  
  faqs: [
    { question: "What is the minimum number of partners for LLP?", answer: "Minimum 2 partners required. No maximum limit. At least one partner must be Indian resident (stayed 182+ days in previous year)." },
    { question: "Can a company be a partner in LLP?", answer: "Yes, companies and LLPs can be partners. They act through their designated representatives/nominees." },
    { question: "Is LLP Agreement mandatory?", answer: "Yes, LLP Agreement must be filed within 30 days of incorporation. It defines rights, duties, and profit sharing among partners." },
    { question: "What's the difference between LLP and Pvt Ltd?", answer: "LLP has lower compliance, no minimum capital, and tax efficiency. Pvt Ltd is better for raising equity investment and has clearer ownership structure for investors." },
    { question: "Can LLP be converted to Private Limited?", answer: "Yes, LLP can be converted to Pvt Ltd company. The process involves specific procedures under Companies Act." },
    { question: "What are annual compliances for LLP?", answer: "Annual return (Form 11) and Statement of Accounts (Form 8) must be filed. Tax audit required if turnover exceeds ₹1 crore or capital exceeds ₹25 lakh." }
  ],
  
  relatedServices: [
    { id: "private-limited-company", label: "Pvt Ltd Company" },
    { id: "partnership-firm", label: "Partnership Firm" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "llp-to-private-limited", label: "LLP to Pvt Ltd" },
    { id: "annual-filing", label: "Annual Compliance" },
    { id: "trademark-registration", label: "Trademark" }
  ],
  
  trustSignals: {
    incorporations: "8,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const onePersonCompany = {
  id: "one-person-company",
  slug: "one-person-company",
  seoTitle: "One Person Company Registration | OPC India",
  seoDescription: "Register your One Person Company (OPC) for single-owner businesses. Get limited liability with sole ownership. Expert OPC incorporation services.",
  heroTitle: "One Person Company (OPC)",
  heroSubtitle: "Business Incorporation",
  summary: "One Person Company (OPC) allows a single entrepreneur to enjoy limited liability benefits while maintaining complete control. It's the perfect structure for solo entrepreneurs who want corporate protection without partners. OPC provides the credibility of a private limited company with simplified compliance.",
  
  whoNeeds: {
    title: "Who Should Register an OPC?",
    description: "OPC is designed for individual entrepreneurs seeking limited liability without partners.",
    points: [
      "Solo entrepreneurs starting new ventures",
      "Freelancers and consultants formalizing business",
      "Proprietors seeking limited liability protection",
      "Individual professionals offering services",
      "Single-owner e-commerce businesses",
      "Entrepreneurs testing business ideas",
      "Individuals with paid-up capital under ₹50 lakh",
      "Businesses with turnover under ₹2 crore"
    ]
  },
  
  whyRequired: {
    title: "Why Choose OPC Structure?",
    description: "OPC offers unique benefits for single entrepreneurs seeking corporate protection.",
    points: [
      "Single Ownership: Complete business control without partners or shareholders.",
      "Limited Liability: Personal assets protected from business liabilities.",
      "Credibility: 'OPC Private Limited' suffix enhances professional image.",
      "Easy Compliance: Reduced compliance burden compared to regular companies.",
      "Perpetual Existence: Company continues through nominee director system.",
      "Easy Conversion: Can convert to Pvt Ltd when business grows."
    ]
  },
  
  benefits: [
    { title: "Sole Control", description: "Single person owns and controls entire company without interference." },
    { title: "Limited Liability", description: "Personal assets protected; liability limited to share capital." },
    { title: "Corporate Status", description: "Separate legal entity with enhanced market credibility." },
    { title: "Lower Compliance", description: "Exempt from many requirements applicable to regular companies." },
    { title: "Business Continuity", description: "Nominee director ensures continuity in case of death/incapacity." },
    { title: "Bank Loans", description: "Easier access to business loans compared to proprietorship." },
    { title: "Tax Benefits", description: "Corporate tax rates and deductions available." },
    { title: "Easy Conversion", description: "Simple conversion to Pvt Ltd when ready to scale." }
  ],
  
  documentsRequired: {
    title: "Documents for OPC Registration",
    description: "Documentation required for One Person Company incorporation.",
    directors: [
      "PAN card of member and nominee",
      "Aadhaar card/Voter ID/Passport",
      "Address proof (recent utility bill/bank statement)",
      "Passport-size photographs",
      "Nominee's consent letter (INC-3)"
    ],
    company: [
      "Proposed company names (2-3 options)",
      "Registered office address proof",
      "Rent agreement or sale deed",
      "NOC from property owner",
      "Utility bill of premises",
      "Business activity description"
    ]
  },
  
  process: [
    { step: 1, title: "DSC & DIN", description: "Obtain Digital Signature Certificate and Director Identification Number.", duration: "1-2 days" },
    { step: 2, title: "Name Reservation", description: "Reserve unique company name through RUN service on MCA.", duration: "2-3 days" },
    { step: 3, title: "Nominee Consent", description: "Obtain nominee's consent using Form INC-3.", duration: "1 day" },
    { step: 4, title: "SPICe+ Filing", description: "Submit incorporation application with MOA, AOA, and nominee consent.", duration: "1 day" },
    { step: 5, title: "MCA Processing", description: "MCA processes application and verifies documents.", duration: "3-5 days" },
    { step: 6, title: "Certificate", description: "Receive Certificate of Incorporation with CIN, PAN, TAN.", duration: "On approval" }
  ],
  
  timeline: {
    total: "7-12 business days",
    breakdown: [
      { phase: "DSC & DIN", duration: "1-2 days" },
      { phase: "Name approval", duration: "2-3 days" },
      { phase: "Incorporation filing", duration: "3-5 days" },
      { phase: "Certificate issuance", duration: "1-2 days" }
    ],
    note: "Nominee consent is mandatory. OPC must convert to Pvt Ltd if capital exceeds ₹50 lakh or turnover ₹2 crore."
  },
  
  pricing: {
    title: "OPC Registration Pricing",
    description: "Complete OPC incorporation packages.",
    packages: [
      {
        name: "Basic",
        price: "₹6,999",
        description: "OPC incorporation only",
        features: [
          "DSC for member",
          "DIN application",
          "Name reservation",
          "SPICe+ incorporation",
          "PAN & TAN"
        ]
      },
      {
        name: "Standard",
        price: "₹11,999",
        description: "OPC + essential registrations",
        features: [
          "Everything in Basic",
          "GST registration",
          "Company stamp",
          "Letterhead design",
          "Compliance calendar"
        ],
        popular: true
      },
      {
        name: "Premium",
        price: "₹21,999",
        description: "Complete business launch",
        features: [
          "Everything in Standard",
          "MSME registration",
          "Trademark filing",
          "First year filing",
          "Virtual CFO consultation"
        ]
      }
    ],
    governmentFees: "Government fees approximately ₹3,000-5,000 including stamp duty and filing fees."
  },
  
  faqs: [
    { question: "Who can form an OPC?", answer: "Only Indian citizens who are Indian residents (stayed 182+ days in India in previous year) can form OPC. NRIs, foreign nationals, and existing companies cannot form OPC." },
    { question: "Who is a nominee in OPC?", answer: "Nominee is a person who becomes member in case of death or incapacity of original member. Nominee must also be Indian citizen and resident." },
    { question: "What are OPC conversion thresholds?", answer: "OPC must convert to Pvt Ltd within 6 months if paid-up capital exceeds ₹50 lakh OR turnover exceeds ₹2 crore for 3 consecutive years." },
    { question: "Can OPC have employees?", answer: "Yes, OPC can hire unlimited employees. The single ownership restriction is only for members/shareholders." },
    { question: "Is audit mandatory for OPC?", answer: "Yes, annual audit is mandatory. However, OPC is exempt from holding AGM and certain board meeting requirements." },
    { question: "Can OPC be converted to LLP?", answer: "Direct conversion from OPC to LLP is not permitted. OPC must first convert to Pvt Ltd, then to LLP." }
  ],
  
  relatedServices: [
    { id: "private-limited-company", label: "Pvt Ltd Company" },
    { id: "sole-proprietorship", label: "Proprietorship" },
    { id: "opc-to-private-limited", label: "OPC to Pvt Ltd" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "annual-filing", label: "Annual Compliance" },
    { id: "income-tax-filing", label: "Tax Filing" }
  ],
  
  trustSignals: {
    incorporations: "5,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const partnershipFirm = {
  id: "partnership-firm",
  slug: "partnership-firm",
  seoTitle: "Partnership Firm Registration Online India",
  seoDescription: "Register your Partnership Firm with expert guidance. Flexible business structure with shared management. Partnership deed drafting and registration services.",
  heroTitle: "Partnership Firm Registration",
  heroSubtitle: "Business Incorporation",
  summary: "Partnership Firm is a traditional business structure where two or more persons agree to share profits of a business. Governed by Indian Partnership Act, 1932, it offers operational flexibility with minimal compliance. Registration is optional but recommended for legal benefits.",
  
  whoNeeds: {
    title: "Who Should Register Partnership Firm?",
    description: "Partnership is ideal for businesses run by two or more persons sharing profits and responsibilities.",
    points: [
      "Family businesses with multiple owners",
      "Professionals practicing together",
      "Small trading and retail businesses",
      "Joint business ventures",
      "Agricultural and farming businesses",
      "Traditional businesses preferring simplicity",
      "Businesses not requiring limited liability",
      "Short-term joint business projects"
    ]
  },
  
  whyRequired: {
    title: "Why Register Partnership Firm?",
    description: "While registration is optional, it provides significant legal advantages.",
    points: [
      "Sue in Court: Only registered firms can sue third parties for contract enforcement.",
      "Claim Set-off: Can claim set-off in legal proceedings against third parties.",
      "Property Rights: Easier to hold property in firm name with registration.",
      "Bank Account: Many banks require registration for partnership accounts.",
      "Legal Recognition: Registration provides official recognition and credibility.",
      "GST Requirement: Partnership deed is required for GST registration."
    ]
  },
  
  benefits: [
    { title: "Easy Formation", description: "Simple process with minimal documentation and compliance." },
    { title: "Flexibility", description: "Partners can customize profit sharing and management through deed." },
    { title: "Lower Costs", description: "Minimal incorporation and compliance costs." },
    { title: "Direct Control", description: "Partners directly manage business without board formalities." },
    { title: "Privacy", description: "Partnership details not publicly accessible like companies." },
    { title: "Tax Benefits", description: "Partners taxed individually; no entity-level taxation." },
    { title: "Easy Dissolution", description: "Simpler process to wind up compared to companies." },
    { title: "Combined Resources", description: "Pool capital, skills, and resources of multiple partners." }
  ],
  
  documentsRequired: {
    title: "Documents for Partnership Registration",
    description: "Documentation required for partnership firm registration.",
    directors: [
      "PAN card of all partners",
      "Aadhaar card/Voter ID of partners",
      "Address proof of all partners",
      "Passport-size photographs"
    ],
    company: [
      "Partnership deed (we help draft)",
      "Affidavit of partners",
      "Proof of registered office address",
      "Rent agreement/ownership proof",
      "Utility bill of premises",
      "Bank account proof of partners"
    ]
  },
  
  process: [
    { step: 1, title: "Partnership Deed", description: "Draft comprehensive partnership deed with profit sharing, duties, and capital details.", duration: "1-2 days" },
    { step: 2, title: "Stamp Duty", description: "Get partnership deed printed on appropriate stamp paper.", duration: "1 day" },
    { step: 3, title: "Notarization", description: "Get deed notarized (optional but recommended).", duration: "1 day" },
    { step: 4, title: "Application Filing", description: "Submit Form-A with deed and documents to Registrar of Firms.", duration: "1 day" },
    { step: 5, title: "Verification", description: "Registrar verifies documents and partnership details.", duration: "7-15 days" },
    { step: 6, title: "Registration Certificate", description: "Receive Certificate of Registration with firm registration number.", duration: "On approval" }
  ],
  
  timeline: {
    total: "15-30 days",
    breakdown: [
      { phase: "Deed drafting", duration: "1-2 days" },
      { phase: "Documentation", duration: "2-3 days" },
      { phase: "Filing & processing", duration: "10-20 days" },
      { phase: "Certificate issuance", duration: "2-5 days" }
    ],
    note: "Timeline varies by state. Some states process faster while others may take longer."
  },
  
  pricing: {
    title: "Partnership Registration Fees",
    description: "Complete partnership formation and registration services.",
    packages: [
      {
        name: "Deed Only",
        price: "₹2,999",
        description: "Partnership deed drafting",
        features: [
          "Comprehensive deed drafting",
          "Stamp paper arrangement",
          "Partner consultation",
          "Notarization assistance",
          "PAN application"
        ]
      },
      {
        name: "Registered",
        price: "₹5,999",
        description: "Deed + Registration",
        features: [
          "Everything in Deed Only",
          "Registrar filing",
          "Registration certificate",
          "GST registration",
          "Bank account assistance"
        ],
        popular: true
      },
      {
        name: "Business Ready",
        price: "₹9,999",
        description: "Complete business setup",
        features: [
          "Everything in Registered",
          "MSME registration",
          "Shop establishment",
          "Compliance guidance",
          "First year support"
        ]
      }
    ],
    governmentFees: "Stamp duty varies by state (₹500-5,000). Registration fees approximately ₹500-2,000."
  },
  
  faqs: [
    { question: "Is partnership registration mandatory?", answer: "No, registration is optional but highly recommended. Unregistered firms cannot sue third parties in court." },
    { question: "What's the minimum number of partners?", answer: "Minimum 2 partners. Maximum 50 for any business, earlier limit of 20 has been removed." },
    { question: "Can an HUF be a partner?", answer: "HUF (Hindu Undivided Family) cannot be a partner as it's not a legal person. However, Karta can be partner in individual capacity." },
    { question: "What's the difference between partnership and LLP?", answer: "Partnership has unlimited liability; partners personally liable for debts. LLP has limited liability protecting personal assets. LLP has more compliance but better protection." },
    { question: "Can partnership be converted to company?", answer: "Yes, partnership can be converted to private limited company or LLP following specific procedures." },
    { question: "What happens if one partner dies?", answer: "Unless deed provides otherwise, partnership dissolves on death of partner. Deed should include succession clauses." }
  ],
  
  relatedServices: [
    { id: "llp-registration", label: "LLP Registration" },
    { id: "private-limited-company", label: "Pvt Ltd Company" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "msme-registration", label: "MSME Registration" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "shop-establishment", label: "Shop License" }
  ],
  
  trustSignals: {
    incorporations: "6,000+",
    rating: "4.7/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

export const soleProprietorship = {
  id: "sole-proprietorship",
  slug: "sole-proprietorship",
  seoTitle: "Sole Proprietorship Registration | Single Owner Business",
  seoDescription: "Register your Sole Proprietorship firm. Simplest business structure for single owners. Get GST, MSME, and bank account with proprietorship setup.",
  heroTitle: "Sole Proprietorship",
  heroSubtitle: "Business Registration",
  summary: "Sole Proprietorship is the simplest form of business owned and operated by a single individual. There's no legal distinction between owner and business. It requires minimal compliance and is ideal for small businesses, freelancers, and individuals starting their entrepreneurial journey.",
  
  whoNeeds: {
    title: "Who Should Start a Proprietorship?",
    description: "Proprietorship is ideal for individual entrepreneurs with small-scale operations.",
    points: [
      "Small shop owners and retailers",
      "Freelancers and consultants",
      "Individual service providers",
      "Small-scale manufacturers",
      "Home-based business owners",
      "Traders and commission agents",
      "Professionals starting practice",
      "Small food and catering businesses"
    ]
  },
  
  whyRequired: {
    title: "Why Choose Proprietorship?",
    description: "Proprietorship offers simplest structure with minimal compliance burden.",
    points: [
      "Easiest Formation: No registration required; can start immediately.",
      "Full Control: Complete decision-making authority without partners.",
      "Minimal Compliance: Only basic tax and license requirements.",
      "Lower Costs: No incorporation fees or annual filing requirements.",
      "Privacy: Business details remain private unlike companies.",
      "Direct Profits: All profits belong to proprietor without sharing."
    ]
  },
  
  benefits: [
    { title: "Quick Start", description: "Start operations immediately without formal registration process." },
    { title: "Full Control", description: "Complete authority over all business decisions and operations." },
    { title: "Simple Tax", description: "Business income taxed as personal income; simple filing." },
    { title: "Low Cost", description: "Minimal setup and compliance costs." },
    { title: "Flexibility", description: "Change business activities easily without formalities." },
    { title: "Direct Profits", description: "All profits belong to owner; no profit sharing." },
    { title: "Easy Closure", description: "Simple process to close business if needed." },
    { title: "Privacy", description: "Business operations and financials remain private." }
  ],
  
  documentsRequired: {
    title: "Documents for Proprietorship",
    description: "Basic documentation to establish proprietorship identity.",
    directors: [
      "PAN card of proprietor",
      "Aadhaar card",
      "Passport-size photographs",
      "Address proof",
      "Bank statement"
    ],
    company: [
      "Business address proof",
      "Rent agreement (if rented)",
      "Electricity bill",
      "NOC from owner (if rented)",
      "Business name declaration"
    ]
  },
  
  process: [
    { step: 1, title: "Choose Business Name", description: "Select a unique business name for your proprietorship.", duration: "Same day" },
    { step: 2, title: "Open Bank Account", description: "Open current account in business name with proprietor KYC.", duration: "1-2 days" },
    { step: 3, title: "GST Registration", description: "Apply for GST if turnover exceeds threshold or for credibility.", duration: "3-5 days" },
    { step: 4, title: "MSME/Udyam", description: "Register under MSME for government benefits.", duration: "Same day" },
    { step: 5, title: "Shop License", description: "Obtain Shop & Establishment license from local authority.", duration: "7-15 days" },
    { step: 6, title: "Start Operations", description: "Begin business operations with required registrations.", duration: "Immediate" }
  ],
  
  timeline: {
    total: "3-7 business days",
    breakdown: [
      { phase: "Bank account opening", duration: "1-2 days" },
      { phase: "GST registration", duration: "2-5 days" },
      { phase: "MSME registration", duration: "Same day" },
      { phase: "Shop license", duration: "Variable" }
    ],
    note: "No formal incorporation required. Registration timeline is for supporting documents."
  },
  
  pricing: {
    title: "Proprietorship Setup Fees",
    description: "Complete proprietorship establishment package.",
    packages: [
      {
        name: "Essential",
        price: "₹1,999",
        description: "Basic registrations",
        features: [
          "GST registration",
          "MSME/Udyam registration",
          "Bank account guidance",
          "Trade name registration",
          "Digital documents"
        ]
      },
      {
        name: "Complete",
        price: "₹3,999",
        description: "Full business setup",
        features: [
          "Everything in Essential",
          "Shop & Establishment",
          "Trade license assistance",
          "Letterhead & visiting cards",
          "Compliance guidance"
        ],
        popular: true
      },
      {
        name: "Professional",
        price: "₹6,999",
        description: "For service providers",
        features: [
          "Everything in Complete",
          "Professional tax registration",
          "TDS registration",
          "First year ITR filing",
          "Accounting software setup"
        ]
      }
    ],
    governmentFees: "GST registration is free. MSME is free. Other fees vary by state."
  },
  
  faqs: [
    { question: "Is proprietorship registration mandatory?", answer: "No formal registration required. However, GST, Shop License, and MSME registration may be needed based on business type and turnover." },
    { question: "What are the liabilities of proprietor?", answer: "Proprietor has unlimited liability. Personal assets can be used to settle business debts. No legal separation between owner and business." },
    { question: "Can proprietorship be converted to company?", answer: "Yes, proprietorship can be converted to Pvt Ltd company. Assets and business are transferred to new company." },
    { question: "What taxes apply to proprietorship?", answer: "Business income is added to personal income and taxed at individual slab rates. GST applies if turnover exceeds threshold." },
    { question: "Can proprietorship have employees?", answer: "Yes, proprietorship can hire employees. Must comply with labor laws, PF, ESI based on employee count." },
    { question: "What's difference between proprietorship and OPC?", answer: "Proprietorship has unlimited liability and informal structure. OPC is a company with limited liability but has compliance requirements." }
  ],
  
  relatedServices: [
    { id: "one-person-company", label: "OPC Registration" },
    { id: "private-limited-company", label: "Pvt Ltd Company" },
    { id: "gst-registration", label: "GST Registration" },
    { id: "msme-registration", label: "MSME Registration" },
    { id: "shop-establishment", label: "Shop License" },
    { id: "income-tax-filing", label: "Tax Filing" }
  ],
  
  trustSignals: {
    incorporations: "15,000+",
    rating: "4.6/5",
    experience: "10+ Years",
    support: "Expert Support"
  }
};

// ==================== GST SERVICES ====================

export const gstReturnFiling = {
  id: "gst-return-filing",
  slug: "gst-return-filing",
  seoTitle: "GST Return Filing Services | GSTR-1, GSTR-3B Filing",
  seoDescription: "Professional GST return filing services. Monthly/quarterly GSTR-1, GSTR-3B filing with ITC reconciliation. Expert CA assistance for accurate compliance.",
  heroTitle: "GST Return Filing",
  heroSubtitle: "Tax Compliance",
  summary: "GST Return Filing is mandatory for all registered taxpayers to report sales, purchases, tax collected, and Input Tax Credit claimed. Our expert team ensures timely and accurate filing of GSTR-1, GSTR-3B, and other returns with proper ITC reconciliation to maximize your tax benefits.",
  
  whoNeeds: {
    title: "Who Needs GST Return Filing?",
    description: "All GST registered businesses must file returns regardless of transaction volume.",
    points: [
      "All GST registered businesses",
      "E-commerce sellers and operators",
      "Exporters and importers",
      "Service providers above threshold",
      "Interstate suppliers of goods",
      "Composition scheme taxpayers",
      "Input service distributors",
      "Non-resident taxable persons"
    ]
  },
  
  whyRequired: {
    title: "Why is Timely GST Filing Critical?",
    description: "GST compliance is legally mandated with significant penalties for non-compliance.",
    points: [
      "Legal Requirement: Filing is mandatory under GST law regardless of business activity.",
      "Avoid Penalties: Late filing attracts ₹50/day for GSTR-3B and ₹200/day for GSTR-1.",
      "Interest Charges: 18% annual interest on late tax payments.",
      "ITC Blocking: Delayed filing blocks Input Tax Credit for your buyers.",
      "GST Cancellation: Continuous non-filing can lead to GST registration cancellation.",
      "E-Way Bill Block: Non-filers cannot generate e-way bills for goods transport."
    ]
  },
  
  benefits: [
    { title: "Timely Compliance", description: "Never miss a due date with proactive filing management." },
    { title: "ITC Optimization", description: "Maximize Input Tax Credit through proper reconciliation." },
    { title: "Error Prevention", description: "Expert review prevents mismatches and notices." },
    { title: "Penalty Avoidance", description: "Timely filing saves late fees and interest." },
    { title: "Reconciliation", description: "GSTR-2A/2B matching for accurate ITC claims." },
    { title: "Expert Support", description: "CA-assisted filing with query resolution." },
    { title: "Documentation", description: "Proper record keeping for future audits." },
    { title: "Peace of Mind", description: "Focus on business while we handle compliance." }
  ],
  
  documentsRequired: {
    title: "Documents for GST Filing",
    description: "Monthly/quarterly documents needed for accurate return filing.",
    directors: [
      "GST portal login credentials",
      "Authorized signatory details",
      "Digital signature (if applicable)"
    ],
    company: [
      "Sales invoices for the period",
      "Purchase invoices with GST",
      "Credit/debit notes issued",
      "Bank statements",
      "Previous period returns",
      "ITC reversal details (if any)",
      "Export documents (for exporters)"
    ]
  },
  
  process: [
    { step: 1, title: "Data Collection", description: "Collect all sales, purchase invoices, and transaction data for the period.", duration: "1-2 days" },
    { step: 2, title: "Invoice Reconciliation", description: "Match purchase invoices with GSTR-2A/2B for ITC verification.", duration: "1-2 days" },
    { step: 3, title: "GSTR-1 Preparation", description: "Prepare and file outward supply details (sales) in GSTR-1.", duration: "1 day" },
    { step: 4, title: "GSTR-3B Calculation", description: "Calculate tax liability, ITC, and net payable/refund.", duration: "1 day" },
    { step: 5, title: "Review & Verification", description: "Expert review of all figures before final submission.", duration: "Same day" },
    { step: 6, title: "Filing & Payment", description: "Submit returns and assist with tax payment if applicable.", duration: "Same day" }
  ],
  
  timeline: {
    total: "2-4 business days",
    breakdown: [
      { phase: "Data collection", duration: "1-2 days" },
      { phase: "Reconciliation", duration: "1 day" },
      { phase: "Filing", duration: "1 day" }
    ],
    note: "GSTR-1 due by 11th, GSTR-3B by 20th of following month. Quarterly filers have different dates."
  },
  
  pricing: {
    title: "GST Return Filing Fees",
    description: "Affordable filing packages based on transaction volume.",
    packages: [
      {
        name: "Starter",
        price: "₹499/month",
        description: "Up to 25 invoices",
        features: [
          "GSTR-1 filing",
          "GSTR-3B filing",
          "Basic ITC reconciliation",
          "Email support",
          "Due date reminders"
        ]
      },
      {
        name: "Growth",
        price: "₹999/month",
        description: "Up to 100 invoices",
        features: [
          "Everything in Starter",
          "Detailed reconciliation",
          "GSTR-2A/2B matching",
          "Phone support",
          "Amendment handling"
        ],
        popular: true
      },
      {
        name: "Business",
        price: "₹2,499/month",
        description: "Up to 500 invoices",
        features: [
          "Everything in Growth",
          "Dedicated expert",
          "Same day filing",
          "Notice handling",
          "Quarterly review calls"
        ]
      }
    ],
    governmentFees: "No government fees for filing. Tax payment as per liability."
  },
  
  faqs: [
    { question: "What is the due date for GST returns?", answer: "GSTR-1: 11th of following month. GSTR-3B: 20th of following month (varies by state for quarterly filers). GSTR-9: 31st December of next FY." },
    { question: "What happens if I file late?", answer: "Late filing attracts: GSTR-3B: ₹50/day (₹20 for nil). GSTR-1: ₹200/day. Maximum capped at ₹10,000 per return. Plus 18% interest on late tax payment." },
    { question: "Can I file nil returns?", answer: "Yes, nil returns must be filed if no transactions occurred. Penalty for late nil filing is ₹20/day capped at ₹500 per return." },
    { question: "What is GSTR-2A/2B matching?", answer: "GSTR-2A/2B shows purchases reported by your suppliers. Matching ensures you claim ITC only for valid, reported purchases. Mismatches can lead to notices." },
    { question: "Do I need to file if GST is canceled?", answer: "You must file final return (GSTR-10) within 3 months of cancellation. Pending returns before cancellation must also be filed." },
    { question: "What about composition scheme filing?", answer: "Composition dealers file quarterly CMP-08 and annual GSTR-4. Different rates and simplified compliance apply." }
  ],
  
  relatedServices: [
    { id: "gst-registration", label: "GST Registration" },
    { id: "gst-annual-return", label: "GSTR-9 Annual Return" },
    { id: "gst-lut-filing", label: "LUT Filing" },
    { id: "e-way-bill", label: "E-Way Bill" },
    { id: "income-tax-filing", label: "ITR Filing" },
    { id: "tds-filing", label: "TDS Filing" }
  ],
  
  trustSignals: {
    incorporations: "25,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

export const incomeTaxFiling = {
  id: "income-tax-filing",
  slug: "income-tax-filing",
  seoTitle: "Income Tax Return Filing | ITR Filing Online India",
  seoDescription: "Professional ITR filing services for individuals and businesses. Expert CA assistance for salary, business, capital gains tax returns. Maximize deductions and refunds.",
  heroTitle: "Income Tax Return Filing",
  heroSubtitle: "Tax Services",
  summary: "Income Tax Return (ITR) filing is mandatory for individuals and businesses meeting income thresholds. Our expert CAs help you file accurate returns, claim all eligible deductions, and maximize refunds. We handle all ITR forms from simple salary income to complex business and capital gains.",
  
  whoNeeds: {
    title: "Who Must File Income Tax Return?",
    description: "ITR filing is mandatory based on income levels and specific criteria.",
    points: [
      "Individuals with income above ₹2.5 lakh (₹3 lakh for seniors)",
      "Anyone wanting to claim income tax refund",
      "Those with foreign assets or foreign income",
      "Directors of companies",
      "Business owners regardless of profit/loss",
      "Individuals who deposited ₹50 lakh+ in savings accounts",
      "Those who spent ₹2 lakh+ on foreign travel",
      "Anyone with TDS deducted seeking refund"
    ]
  },
  
  whyRequired: {
    title: "Why is ITR Filing Important?",
    description: "ITR filing is not just compliance but offers multiple benefits.",
    points: [
      "Legal Requirement: Mandatory for income above threshold or specified categories.",
      "Claim Refunds: Get back excess TDS deducted from your income.",
      "Loan Applications: Banks require ITR for home, car, and business loans.",
      "Visa Processing: Most countries require ITR proof for visa applications.",
      "Carry Forward Losses: Business/capital losses can be carried forward only if ITR filed.",
      "Avoid Penalties: Late filing attracts penalties up to ₹10,000."
    ]
  },
  
  benefits: [
    { title: "Maximum Deductions", description: "Claim all eligible deductions under 80C, 80D, HRA, etc." },
    { title: "Accurate Filing", description: "Expert preparation ensures error-free returns." },
    { title: "Refund Optimization", description: "Get maximum refund with proper TDS reconciliation." },
    { title: "Audit Support", description: "Documentation and support for scrutiny cases." },
    { title: "Loss Set-off", description: "Properly set-off and carry forward losses." },
    { title: "Notice Handling", description: "Expert response to any income tax notices." },
    { title: "Future Planning", description: "Tax planning advice for better savings." },
    { title: "Quick Processing", description: "Fast filing with status tracking." }
  ],
  
  documentsRequired: {
    title: "Documents for ITR Filing",
    description: "Comprehensive document list for accurate tax filing.",
    directors: [
      "PAN card",
      "Aadhaar card",
      "Bank account details",
      "Email ID and mobile number"
    ],
    company: [
      "Form 16 (for salaried)",
      "Form 26AS (tax credit statement)",
      "Bank statements",
      "Investment proofs (80C, 80D, etc.)",
      "Home loan interest certificate",
      "Capital gains statements",
      "Rental income details",
      "Business P&L (for business income)"
    ]
  },
  
  process: [
    { step: 1, title: "Document Collection", description: "Gather Form 16, Form 26AS, investment proofs, and income details.", duration: "1-2 days" },
    { step: 2, title: "Income Computation", description: "Calculate total income from all sources with applicable exemptions.", duration: "1 day" },
    { step: 3, title: "Deduction Optimization", description: "Identify and apply all eligible deductions to minimize tax.", duration: "Same day" },
    { step: 4, title: "ITR Preparation", description: "Prepare appropriate ITR form based on income sources.", duration: "1 day" },
    { step: 5, title: "Review & Verification", description: "Expert review ensuring accuracy and compliance.", duration: "Same day" },
    { step: 6, title: "E-Filing & E-Verify", description: "Submit return online and complete e-verification.", duration: "Same day" }
  ],
  
  timeline: {
    total: "1-3 business days",
    breakdown: [
      { phase: "Document collection", duration: "1 day" },
      { phase: "Return preparation", duration: "1 day" },
      { phase: "Filing & verification", duration: "Same day" }
    ],
    note: "Due date: July 31 for individuals, October 31 for audit cases. Belated return till December 31."
  },
  
  pricing: {
    title: "ITR Filing Fees",
    description: "Transparent pricing based on income complexity.",
    packages: [
      {
        name: "Salaried",
        price: "₹999",
        description: "Simple salary income",
        features: [
          "Single Form 16",
          "Standard deductions",
          "80C, 80D claims",
          "Basic HRA exemption",
          "E-filing & e-verification"
        ]
      },
      {
        name: "Professional",
        price: "₹2,499",
        description: "Multiple income sources",
        features: [
          "Salary + other income",
          "Capital gains",
          "Rental income",
          "Multiple Form 16",
          "Detailed tax planning"
        ],
        popular: true
      },
      {
        name: "Business",
        price: "₹4,999",
        description: "Business/professional income",
        features: [
          "Business income (44AD/44ADA)",
          "All income sources",
          "Loss carry forward",
          "Audit assistance",
          "Notice support"
        ]
      }
    ],
    governmentFees: "No government fees for e-filing. Late filing fees: ₹1,000-10,000 based on income."
  },
  
  faqs: [
    { question: "What is the due date for ITR filing?", answer: "July 31 for individuals and non-audit cases. October 31 for audit cases. Belated/revised returns till December 31 with late fees." },
    { question: "Which ITR form should I use?", answer: "ITR-1: Salaried up to ₹50L. ITR-2: Capital gains, multiple properties. ITR-3: Business income. ITR-4: Presumptive business. Our experts select the right form." },
    { question: "What is the penalty for late filing?", answer: "₹1,000 if income under ₹5 lakh. ₹5,000 if filed by December 31. ₹10,000 if filed after December 31. Interest on tax due at 1% per month." },
    { question: "How long to get refund?", answer: "Typically 20-45 days after e-verification if return is processed without issues. Delays possible if selected for processing queries." },
    { question: "Can I revise my ITR?", answer: "Yes, revised return can be filed till December 31 of assessment year or before assessment completion, whichever is earlier." },
    { question: "What if I miss the deadline?", answer: "Belated return can be filed till December 31 with late fees. Some benefits like loss carry-forward are lost. Beyond December, only updated return possible with additional tax." }
  ],
  
  relatedServices: [
    { id: "tds-filing", label: "TDS Filing" },
    { id: "tax-planning", label: "Tax Planning" },
    { id: "gst-return-filing", label: "GST Returns" },
    { id: "ca-certification", label: "CA Certification" },
    { id: "audit-services", label: "Tax Audit" },
    { id: "virtual-cfo", label: "Virtual CFO" }
  ],
  
  trustSignals: {
    incorporations: "50,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

// Export all service objects
export const incorporationServices = {
  llpRegistration,
  onePersonCompany,
  partnershipFirm,
  soleProprietorship
};

export const taxServices = {
  gstReturnFiling,
  incomeTaxFiling
};
